import React from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'

import xhr from 'xhr'

const fullName = (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim()

export default function LinkStudentModal({ isOpen, onClose, prospect, initialStudentId, onLinked }) {
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState(null)
  const [searching, setSearching] = React.useState(false)
  const [linking, setLinking] = React.useState(null)
  const [error, setError] = React.useState('')

  const search = async (value) => {
    const term = String(value || '').trim()
    if (!term) return
    setSearching(true)
    setError('')
    try {
      const encoded = encodeURIComponent(term)
      const filters = [
        `filters[$or][0][user][email][$eq]=${encodeURIComponent(term.toLowerCase())}`,
        `filters[$or][1][user][cellphone][$eq]=${encoded}`,
      ]
      // Un celular no cabe en un entero de Postgres: solo se compara con el código si es corto.
      if (/^\d{1,9}$/.test(term)) filters.push(`filters[$or][2][id][$eq]=${encoded}`)

      const res = await xhr(
        `/students?${filters.join('&')}&populate[user]=true&populate[adviser][populate]=user&populate[origin_prospect]=true&pagination[limit]=10`
      )
      setResults(res.data.data)
    } catch (err) {
      console.error(err)
      setError('No se pudo buscar. Inténtalo de nuevo.')
    } finally {
      setSearching(false)
    }
  }

  React.useEffect(() => {
    if (isOpen && initialStudentId) {
      setQuery(String(initialStudentId))
      search(initialStudentId)
    }
    if (!isOpen) {
      setQuery('')
      setResults(null)
      setError('')
    }
  }, [isOpen, initialStudentId])

  const handleLink = async (studentId) => {
    setLinking(studentId)
    setError('')
    try {
      await xhr.post('/advisers/students/link', { prospect: prospect.id, student: studentId })
      onLinked()
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'No se pudo vincular el estudiante.')
    } finally {
      setLinking(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">Vincular estudiante existente</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Para estudiantes que ya tienen cuenta, por ejemplo porque se registraron solos. Busca por correo, celular o
            código.
          </Text>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              search(query)
            }}
          >
            <InputGroup>
              <Input
                id="link-student-query"
                placeholder="Correo, celular o código"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                pr="90px"
              />
              <InputRightElement width="84px">
                <Button size="sm" type="submit" isLoading={searching}>
                  Buscar
                </Button>
              </InputRightElement>
            </InputGroup>
          </form>

          {error && (
            <Alert status="error" borderRadius="md" mt={4} fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Box mt={4}>
            {searching && (
              <Flex justify="center" py={4}>
                <Spinner />
              </Flex>
            )}
            {!searching && results && results.length === 0 && (
              <Text fontSize="sm" color="gray.500">
                No se encontró ningún estudiante. Revisa el dato o registra uno nuevo.
              </Text>
            )}
            {!searching && results && results.length > 0 && (
              <Stack spacing={3}>
                {results.map((student) => {
                  const linkedHere = student.origin_prospect?.id === prospect.id
                  const linkedElsewhere = student.origin_prospect && !linkedHere
                  const adviserName = fullName(student.adviser?.user)
                  return (
                    <Flex
                      key={student.id}
                      justify="space-between"
                      align="center"
                      gap={3}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                    >
                      <Box minW={0}>
                        <Text fontWeight="600" fontSize="sm">
                          {fullName(student.user) || 'Sin nombre'}
                        </Text>
                        <Text fontSize="xs" color="gray.500" wordBreak="break-all">
                          Código {student.id} · {student.user?.email} · {student.user?.cellphone}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Asesora: {adviserName || 'sin asignar'}
                          {linkedElsewhere ? ` · Ya vinculado al prospecto #${student.origin_prospect.id}` : ''}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        colorScheme="purple"
                        variant={linkedHere ? 'ghost' : 'solid'}
                        isDisabled={linkedHere || linkedElsewhere}
                        isLoading={linking === student.id}
                        onClick={() => handleLink(student.id)}
                        flexShrink={0}
                      >
                        {linkedHere ? 'Ya vinculado' : 'Vincular'}
                      </Button>
                    </Flex>
                  )
                })}
              </Stack>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
